/**
 * @fileoverview Complete canonical contact and contact-link schemas for the
 *               complex-inspections contract bundle (document 12).
 * @module import-core/contracts/complex-inspections/contactSchemas
 *
 * Responsibilities:
 * - Define the complete canonical `contacts` document schema
 *   (`canonicalContactDocumentSchema`).
 * - Define every relationship link/snapshot: group links, entity links with
 *   direct/complex origins, case links, event responsible-party snapshots, and
 *   delivery recipient snapshots.
 * - Normalize legacy entity contact references into canonical entity links.
 *
 * Dependencies:
 * - `zod` v3; `./sharedSchemas` primitives and snapshot pieces.
 *
 * Architecture:
 * - Pure schemas + cross-field refinements. The canonical contact stays
 *   normalized — no group/entity/case relationship arrays are permitted on it.
 *
 * Notes:
 * - Origin invariants from document 12 §5 are enforced here:
 *   direct origins carry no `groupId`; complex origins require `groupId` and
 *   `scope='complex-wide'`; originIds are unique per link.
 */

import { z } from 'zod';
import {
  objectIdLikeSchema,
  isoDateLikeSchema,
  lookupReferenceSchema,
  legacySourceSchema,
  postalAddressSchema,
  contactEmailSchema,
  contactPhoneSchema,
  contactAffiliationSchema,
  contactSnapshotSchema,
  auditFieldsSchema,
  tombstoneFieldsSchema,
} from './sharedSchemas';

// ---------------------------------------------------------------------------
// Canonical contacts document (document 12 §2)
// ---------------------------------------------------------------------------

/**
 * @contract canonicalContactDocumentSchema
 * Purpose: Complete normalized canonical contact. Complex support adds no
 *   group/entity/case relationship arrays to this document — relationships
 *   live on the documents that own them.
 * Fields: identity (name/firstName/lastName/displayName), channels,
 *   affiliations, defaultRoles (suggestions only), mailing address, tags,
 *   status, source/legacy provenance, audit + tombstone.
 * Rules: schemaVersion:1 writes require all audit fields and structured
 *   arrays; legacy flat phone/email/role/contactType fields are
 *   compatibility-only and rejected by this strict schema.
 * Sync: `.strict()` at the containing-document boundary.
 */
export const canonicalContactDocumentSchema = z
  .object({
    _id: objectIdLikeSchema,
    schemaVersion: z.literal(1),

    name: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    displayName: z.string(),

    title: z.string().optional(),
    company: z.string().optional(),

    emails: z.array(contactEmailSchema),
    phoneNumbers: z.array(contactPhoneSchema),
    affiliations: z.array(contactAffiliationSchema).optional(),

    defaultRoles: z.array(z.string()),

    mailingAddress: postalAddressSchema.nullable().optional(),
    address: z.string().optional(),

    tags: z.array(z.string()).optional(),
    notes: z.string().optional(),
    isPrimaryContact: z.boolean(),
    status: z.enum(['active', 'inactive']),

    sourceMetadata: z
      .object({
        sourceSystem: z.string().optional(),
        sourceRecordId: z.string().optional(),
        sourceUpdatedAt: isoDateLikeSchema.optional(),
        importRunId: objectIdLikeSchema.optional(),
        importedAt: isoDateLikeSchema.optional(),
      })
      .optional(),
    legacySource: legacySourceSchema.optional(),
  })
  .merge(auditFieldsSchema)
  .merge(tombstoneFieldsSchema)
  .strict();

// ---------------------------------------------------------------------------
// Group contact link (document 12 §4)
// ---------------------------------------------------------------------------

/**
 * @contract complexGroupContactLinkSchema
 * Purpose: One complex-wide contact relationship on
 *   `inspectableEntityGroups.contacts[]`. Roles are complex-scoped
 *   `entityContactRole` lookups; `isPrimary` is primary within the complex
 *   contact set only.
 * Rules: at least one role; exactly one link per contactId on the group
 *   (enforced by the containing document refinement).
 */
export const complexGroupContactLinkSchema = z
  .object({
    schemaVersion: z.literal(1),
    contactId: objectIdLikeSchema,
    roles: z.array(lookupReferenceSchema).min(1),
    isPrimary: z.boolean(),
    notes: z.string().optional(),
    snapshot: contactSnapshotSchema,
    linkedAt: isoDateLikeSchema,
    linkedBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
  })
  .strict()
  .superRefine((link, ctx) => {
    link.roles.forEach((role, index) => {
      if (role.lookupType !== 'entityContactRole') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['roles', index, 'lookupType'],
          message: "group contact roles must use lookupType 'entityContactRole'",
        });
      }
    });
  });

// ---------------------------------------------------------------------------
// Entity contact link + origins (document 12 §5)
// ---------------------------------------------------------------------------

/**
 * @contract entityContactLinkOriginSchema
 * Purpose: One origin record explaining why a contact appears on an entity.
 * Rules: `originType='direct'` ⇒ `scope='entity-only'` and no `groupId`;
 *   `originType='complex'` ⇒ `scope='complex-wide'` and required `groupId`.
 */
export const entityContactLinkOriginSchema = z
  .object({
    originId: z.string(),
    originType: z.enum(['direct', 'complex']),
    scope: z.enum(['entity-only', 'complex-wide']),
    groupId: objectIdLikeSchema.optional(),
    roles: z.array(lookupReferenceSchema),
    isPrimary: z.boolean(),
    linkedAt: isoDateLikeSchema,
    linkedBy: objectIdLikeSchema,
  })
  .strict()
  .superRefine((origin, ctx) => {
    if (origin.originType === 'direct') {
      if (origin.scope !== 'entity-only') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scope'],
          message: "direct origins must use scope 'entity-only'",
        });
      }
      if (origin.groupId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groupId'],
          message: 'direct origins must not carry groupId',
        });
      }
    }
    if (origin.originType === 'complex') {
      if (origin.scope !== 'complex-wide') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['scope'],
          message: "complex origins must use scope 'complex-wide'",
        });
      }
      if (origin.groupId === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groupId'],
          message: 'complex origins require groupId',
        });
      }
    }
  });

/**
 * @contract entityContactLinkSchema
 * Purpose: Entity-visible contact link with origins, materialized
 *   `effectiveRoles`/`isPrimary` projections, and mutable snapshot.
 * Rules: linkOrigins non-empty (a link with no surviving origins is removed,
 *   not persisted); originIds unique within the link.
 */
export const entityContactLinkSchema = z
  .object({
    schemaVersion: z.literal(1),
    contactId: objectIdLikeSchema,
    snapshot: contactSnapshotSchema,
    linkOrigins: z.array(entityContactLinkOriginSchema).min(1),
    effectiveRoles: z.array(lookupReferenceSchema),
    isPrimary: z.boolean(),
    notes: z.string().optional(),
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
    // Read-compatibility only; new writers do not set these.
    contactType: z.enum(['responsible', 'contact']).optional(),
    role: lookupReferenceSchema.optional(),
  })
  .strict()
  .superRefine((link, ctx) => {
    const seen = new Set<string>();
    link.linkOrigins.forEach((origin, index) => {
      if (seen.has(origin.originId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['linkOrigins', index, 'originId'],
          message: 'linkOrigins originIds must be unique within an entity contact link',
        });
      }
      seen.add(origin.originId);
    });

    // Materialized projections must equal the de-duplicated union of origin
    // roles (by lookupId/code) and `isPrimary` must equal `some(origin.isPrimary)`
    // — a stale projection is a data corruption, not a style choice (doc 12 §5).
    const roleKey = (role: { lookupId?: unknown; code?: string }) =>
      `${String(role.lookupId)}::${String(role.code)}`;
    const expectedRoles = new Map<string, (typeof link.linkOrigins)[number]['roles'][number]>();
    link.linkOrigins.forEach((origin) => {
      origin.roles.forEach((role) => {
        const key = roleKey(role);
        if (!expectedRoles.has(key)) expectedRoles.set(key, role);
      });
    });
    const actualKeys = new Set(link.effectiveRoles.map(roleKey));
    if (
      actualKeys.size !== expectedRoles.size ||
      [...expectedRoles.keys()].some((key) => !actualKeys.has(key))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['effectiveRoles'],
        message: 'effectiveRoles must equal the de-duplicated union of linkOrigins roles',
      });
    }
    const expectedPrimary = link.linkOrigins.some((origin) => origin.isPrimary);
    if (link.isPrimary !== expectedPrimary) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['isPrimary'],
        message: 'isPrimary must equal some(linkOrigins.isPrimary)',
      });
    }
  });

const legacyEntityContactChannelSchemas = {
  emails: z.array(
    z.object({
      type: lookupReferenceSchema,
      email: z.string().email(),
      isPrimary: z.boolean(),
    }),
  ),
  phoneNumbers: z.array(
    z.object({
      type: lookupReferenceSchema,
      number: z.string(),
      countryCode: z.string(),
      extension: z.string().optional(),
      isPrimary: z.boolean(),
    }),
  ),
};

/**
 * @contract legacyEntityContactRefSchema
 * Purpose: Backward-compatible input shape for entity `contacts[]` items from
 *   clients that predate canonical relationship links.
 * Fields: contactId, contactType, role, isPrimary, notes, name, title, emails,
 *   phoneNumbers, affiliation, linkedAt.
 * Rules: Input-only; normalize before persisting a schemaVersion:1 entity.
 * Sync: Normalized into `EntityContactLink` before entity document writes.
 */
export const legacyEntityContactRefSchema = z
  .object({
    contactId: objectIdLikeSchema,
    contactType: z.enum(['responsible', 'contact']).optional(),
    role: lookupReferenceSchema.optional(),
    isPrimary: z.boolean().optional(),
    notes: z.string().optional(),
    name: z.string().optional(),
    title: z.string().optional(),
    emails: legacyEntityContactChannelSchemas.emails.optional(),
    phoneNumbers: legacyEntityContactChannelSchemas.phoneNumbers.optional(),
    affiliation: z
      .object({
        organizationName: z.string(),
        department: z.string().optional(),
      })
      .optional(),
    linkedAt: z.string().optional(),
  })
  .strict();

const originlessEntityContactLinkSchema = z
  .object({
    contactId: objectIdLikeSchema,
    schemaVersion: z.unknown().optional(),
    snapshot: contactSnapshotSchema.optional(),
    linkOrigins: z.array(entityContactLinkOriginSchema).max(0).optional(),
    effectiveRoles: z.array(lookupReferenceSchema).optional(),
    isPrimary: z.boolean().optional(),
    notes: z.string().optional(),
    updatedAt: isoDateLikeSchema.optional(),
    updatedBy: objectIdLikeSchema.optional(),
    linkedAt: z.string().optional(),
    contactType: z.enum(['responsible', 'contact']).optional(),
    role: lookupReferenceSchema.optional(),
    name: z.string().optional(),
    title: z.string().optional(),
    emails: legacyEntityContactChannelSchemas.emails.optional(),
    phoneNumbers: legacyEntityContactChannelSchemas.phoneNumbers.optional(),
    affiliation: z
      .object({
        organizationName: z.string(),
        department: z.string().optional(),
      })
      .optional(),
  })
  .passthrough();

/**
 * Fallback actor for links repaired from legacy rows whose audit identity is
 * missing or malformed — the zero-ObjectId system-actor convention used by
 * tenant-api's actorResolver.
 */
const REPAIRED_LINK_ACTOR_HEX = '000000000000000000000000';

/** First candidate that satisfies `objectIdLikeSchema`, else the system actor. */
function firstValidActorId(...candidates: unknown[]): unknown {
  for (const candidate of candidates) {
    if (objectIdLikeSchema.safeParse(candidate).success) return candidate;
  }
  return REPAIRED_LINK_ACTOR_HEX;
}

/** First candidate that satisfies `isoDateLikeSchema`, else undefined. */
function firstValidIsoDate(...candidates: unknown[]): unknown {
  for (const candidate of candidates) {
    if (candidate !== undefined && isoDateLikeSchema.safeParse(candidate).success) return candidate;
  }
  return undefined;
}

/**
 * @process normalizeEntityContactLink
 * Steps: retain canonical links; restore an originless legacy snapshot as one
 *   direct origin; otherwise normalize the pre-canonical flat contact shape.
 * Inputs: ref, writing actor, and current write timestamp.
 * Outputs: a valid canonical `EntityContactLink`.
 * Failure Handling: invalid or ambiguous legacy shapes throw schema errors.
 *   Repaired links with no usable audit identity fall back to the system
 *   actor (zero ObjectId) so legacy imported rows still normalize.
 * Idempotency: REQUIRED — canonical inputs pass through unchanged.
 * @errorHandling
 * Strategy: Parse canonical or supported legacy shapes; reject ambiguous data.
 * User Impact: Invalid links block the containing write with field validation.
 * Logging: No logging here; the owning write boundary reports sanitized errors.
 * @security
 * Validation: All normalized links are parsed by `entityContactLinkSchema`.
 * Trust Boundary: Legacy values remain untrusted until the containing entity
 *   passes complete-document validation.
 */
export function normalizeEntityContactLink(
  ref: unknown,
  actorId: unknown,
  now: Date,
): z.infer<typeof entityContactLinkSchema> {
  const canonical = entityContactLinkSchema.safeParse(ref);
  if (canonical.success) return canonical.data;

  const originless = originlessEntityContactLinkSchema.safeParse(ref);
  if (originless.success) {
    const link = originless.data;
    const roles = link.effectiveRoles?.length
      ? link.effectiveRoles
      : link.role
        ? [link.role]
        : [];
    const isPrimary = link.isPrimary ?? false;
    const linkedAt = firstValidIsoDate(link.linkedAt, link.updatedAt) ?? now;
    const linkedBy = firstValidActorId(link.updatedBy, actorId);
    return entityContactLinkSchema.parse({
      schemaVersion: 1,
      contactId: link.contactId,
      snapshot: link.snapshot ?? {
        displayName: link.name ?? '',
        title: link.title,
        emails: link.emails ?? [],
        phoneNumbers: link.phoneNumbers ?? [],
        affiliations: link.affiliation ? [link.affiliation] : undefined,
        canonicalUpdatedAt: now,
      },
      linkOrigins: [{
        originId: `direct:${String(link.contactId)}`,
        originType: 'direct',
        scope: 'entity-only',
        roles,
        isPrimary,
        linkedAt,
        linkedBy,
      }],
      effectiveRoles: roles,
      isPrimary,
      notes: link.notes,
      updatedAt: firstValidIsoDate(link.updatedAt) ?? now,
      updatedBy: linkedBy,
      ...(link.contactType ? { contactType: link.contactType } : {}),
      ...(link.role ? { role: link.role } : {}),
    });
  }

  const legacy = legacyEntityContactRefSchema.parse(ref);
  const role = legacy.role;
  const legacyActor = firstValidActorId(actorId);
  return entityContactLinkSchema.parse({
    schemaVersion: 1,
    contactId: legacy.contactId,
    snapshot: {
      displayName: legacy.name ?? '',
      title: legacy.title,
      emails: legacy.emails ?? [],
      phoneNumbers: legacy.phoneNumbers ?? [],
      affiliations: legacy.affiliation ? [legacy.affiliation] : undefined,
      canonicalUpdatedAt: now,
    },
    linkOrigins: [{
      originId: `direct:${String(legacy.contactId)}`,
      originType: 'direct',
      scope: 'entity-only',
      roles: role ? [role] : [],
      isPrimary: legacy.isPrimary ?? false,
      linkedAt: firstValidIsoDate(legacy.linkedAt) ?? now,
      linkedBy: legacyActor,
    }],
    effectiveRoles: role ? [role] : [],
    isPrimary: legacy.isPrimary ?? false,
    notes: legacy.notes,
    updatedAt: now,
    updatedBy: legacyActor,
    ...(legacy.contactType ? { contactType: legacy.contactType } : {}),
    ...(role ? { role } : {}),
  });
}

// ---------------------------------------------------------------------------
// Case contact link (document 12 §6)
// ---------------------------------------------------------------------------

/**
 * @contract caseContactLinkSchema
 * Purpose: One entry in the case working-set `cases.contacts[]`. `source`
 *   records how the contact entered the case; `sourceEntityId`/`groupId`
 *   qualify `entity`/`complex` sources.
 */
export const caseContactLinkSchema = z
  .object({
    schemaVersion: z.literal(1),
    contactId: objectIdLikeSchema,
    source: z.enum(['entity', 'complex', 'case-direct']),
    sourceEntityId: objectIdLikeSchema.optional(),
    groupId: objectIdLikeSchema.optional(),
    roles: z.array(lookupReferenceSchema),
    isPrimary: z.boolean(),
    notes: z.string().optional(),
    snapshot: contactSnapshotSchema,
    linkedAt: isoDateLikeSchema,
    linkedBy: objectIdLikeSchema,
    updatedAt: isoDateLikeSchema,
    updatedBy: objectIdLikeSchema,
  })
  .strict()
  .superRefine((link, ctx) => {
    // Source qualifiers (doc 12 §6): `entity` requires sourceEntityId,
    // `complex` requires groupId, `case-direct` carries neither — and no
    // source may carry the unrelated qualifier field.
    if (link.source === 'entity') {
      if (link.sourceEntityId === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sourceEntityId'],
          message: "case contact source 'entity' requires sourceEntityId",
        });
      }
      if (link.groupId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groupId'],
          message: "case contact source 'entity' must not carry groupId",
        });
      }
    } else if (link.source === 'complex') {
      if (link.groupId === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groupId'],
          message: "case contact source 'complex' requires groupId",
        });
      }
      if (link.sourceEntityId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['sourceEntityId'],
          message: "case contact source 'complex' must not carry sourceEntityId",
        });
      }
    } else {
      if (link.sourceEntityId !== undefined || link.groupId !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['source'],
          message: "case contact source 'case-direct' must not carry sourceEntityId or groupId",
        });
      }
    }
  });

/**
 * @contract eventResponsiblePartySnapshotSchema
 * Purpose: Immutable per-event responsible-party selection captured when the
 *   event starts. Never rewritten by later canonical contact edits.
 */
export const eventResponsiblePartySnapshotSchema = z
  .object({
    contactId: objectIdLikeSchema.optional(),
    source: z.enum(['case-contact', 'entity-contact', 'complex-contact', 'ownership', 'manual']),
    groupId: objectIdLikeSchema.optional(),
    name: z.string(),
    company: z.string().optional(),
    title: z.string().optional(),
    roles: z.array(lookupReferenceSchema),
    emails: z.array(contactEmailSchema),
    phoneNumbers: z.array(contactPhoneSchema),
    mailingAddress: postalAddressSchema.nullable().optional(),
    capturedAt: isoDateLikeSchema,
    capturedBy: objectIdLikeSchema,
  })
  .strict()
  .superRefine((snapshot, ctx) => {
    // `complex-contact` is the only source qualified by groupId (doc 12 §7).
    if (snapshot.source === 'complex-contact') {
      if (snapshot.groupId === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groupId'],
          message: "responsible-party source 'complex-contact' requires groupId",
        });
      }
    } else if (snapshot.groupId !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['groupId'],
        message: `responsible-party source '${snapshot.source}' must not carry groupId`,
      });
    }
  });

export const responsiblePartyStatusSchema = z.enum([
  'selected',
  'owner-default',
  'not-required',
  'missing-required',
]);

// ---------------------------------------------------------------------------
// Delivery recipient snapshot (document 12 §8) — immutable historical evidence
// ---------------------------------------------------------------------------

/**
 * @contract inspectionDeliveryRecipientSnapshotSchema
 * Purpose: Exact send-time recipient persisted on delivery records. Never
 *   follows canonical contact edits.
 */
export const inspectionDeliveryRecipientSnapshotSchema = z
  .object({
    recipientId: z.string(),
    recipientKind: z.enum(['canonicalContact', 'deliverySnapshot']),
    contactId: objectIdLikeSchema.optional(),
    name: z.string(),
    company: z.string().optional(),
    title: z.string().optional(),
    role: z.string().optional(),
    deliveryRole: z.enum(['responsibleParty', 'additional']),
    method: z.enum(['email', 'mail', 'post', 'text', 'stannp']),
    status: z.enum(['pending', 'queued', 'sent', 'delivered', 'failed', 'skipped']),
    sentAt: isoDateLikeSchema.optional(),
    failureReason: z.string().optional(),
    emails: z.array(z.string()),
    phoneNumbers: z.array(z.string()).optional(),
    mailingAddress: postalAddressSchema.optional(),
    isPrimary: z.boolean().optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type CanonicalContactDocument = z.infer<typeof canonicalContactDocumentSchema>;
export type ComplexGroupContactLink = z.infer<typeof complexGroupContactLinkSchema>;
export type EntityContactLink = z.infer<typeof entityContactLinkSchema>;
export type EntityContactLinkOrigin = z.infer<typeof entityContactLinkOriginSchema>;
export type CaseContactLink = z.infer<typeof caseContactLinkSchema>;
export type EventResponsiblePartySnapshot = z.infer<typeof eventResponsiblePartySnapshotSchema>;
export type InspectionDeliveryRecipientSnapshot = z.infer<typeof inspectionDeliveryRecipientSnapshotSchema>;
